import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'scorinova_favorites';

function getNextClearTime() {
  const now = new Date();
  const today6am = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 6, 0, 0));
  if (now >= today6am) return today6am;
  // Before 6AM UTC today — previous day's 6AM was the last clear point
  return new Date(today6am.getTime() - 24 * 60 * 60 * 1000);
}

function loadFavorites() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], lastCleared: null };
    return JSON.parse(raw);
  } catch {
    return { items: [], lastCleared: null };
  }
}

function saveFavorites(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function useFavorites() {
  const [favorites, setFavorites] = useState(() => {
    const data = loadFavorites();
    const clearTime = getNextClearTime();
    // Auto-clear if lastCleared is before the most recent 6AM UTC threshold
    if (!data.lastCleared || new Date(data.lastCleared) < clearTime) {
      const cleared = { items: [], lastCleared: clearTime.toISOString() };
      saveFavorites(cleared);
      return cleared.items;
    }
    return data.items;
  });

  // Sync to localStorage on every change
  useEffect(() => {
    const data = loadFavorites();
    saveFavorites({ items: favorites, lastCleared: data.lastCleared || new Date().toISOString() });
  }, [favorites]);

  const toggleFavorite = useCallback((id, type) => {
    setFavorites(prev => {
      const exists = prev.some(f => f.id === id && f.type === type);
      if (exists) {
        return prev.filter(f => !(f.id === id && f.type === type));
      }
      return [...prev, { id, type }];
    });
  }, []);

  const clearFavorites = useCallback(() => {
    setFavorites([]);
    saveFavorites({ items: [], lastCleared: new Date().toISOString() });
  }, []);

  const isFavorite = useCallback((id, type) => {
    return favorites.some(f => f.id === id && f.type === type);
  }, [favorites]);

  const hasFavorites = favorites.length > 0;

  return { favorites, toggleFavorite, clearFavorites, isFavorite, hasFavorites, favoritesCount: favorites.length };
}
